/**
 * Pure transform: Doc<"notifications"> -> InboxItem view-model.
 *
 * Zero side effects, zero console calls, never throws.
 * Strips the raw `payload` field from the output — only `navigateHref`
 * (derived from payload + kind template) survives.
 *
 * Kind lookup uses Object.prototype.hasOwnProperty.call to defend
 * against prototype pollution; unrecognized kinds fall through to the
 * `unknown` entry (passthrough label, "info" icon, null href).
 *
 * @see specs/prd.md §3 FR-006
 */
import type { Doc } from "../../../../../convex/_generated/dataModel";
import type { InboxItem } from "../types/notification";
import { NOTIFICATION_KIND_CONFIG } from "../constants/notificationKinds";
import { formatRelativeTime } from "../utils/time";

/**
 * Transforms a raw notification document into an `InboxItem` view-model.
 *
 * The function is pure: no side effects, no console output, no throws.
 * Unknown kinds degrade gracefully to the `unknown` fallback entry.
 */
export function transformNotification(doc: Doc<"notifications">): InboxItem {
  // Kind lookup with prototype-pollution defense
  const kindEntry = Object.prototype.hasOwnProperty.call(NOTIFICATION_KIND_CONFIG, doc.kind)
    ? NOTIFICATION_KIND_CONFIG[doc.kind]!
    : NOTIFICATION_KIND_CONFIG["unknown"]!;

  // For unrecognized kinds, use the raw kind string as label (passthrough)
  const kindLabel = Object.prototype.hasOwnProperty.call(NOTIFICATION_KIND_CONFIG, doc.kind) && doc.kind !== "unknown"
    ? kindEntry.label
    : (doc.kind as string);

  // Derive navigateHref via the kind's template function, wrapped in try-catch
  let navigateHref: string | null = null;
  try {
    const payload = (doc as Record<string, unknown>).payload as Record<string, unknown> | null;
    navigateHref = kindEntry.navigateHref(payload ?? null);
  } catch {
    // On any failure the field falls back to null — no exception escapes
    navigateHref = null;
  }

  return {
    id: doc._id,
    kind: doc.kind,
    kindLabel,
    icon: kindEntry.icon,
    title: doc.title,
    body: doc.body ?? null,
    navigateHref,
    read: doc.read,
    readAt: doc.readAt ?? null,
    relativeTime: formatRelativeTime(doc._creationTime),
    createdAtMs: doc._creationTime,
  };
}
